import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './auth.store';
import { registerSchema, type RegisterForm } from '@/shared/schemas/auth.schema';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { ApiException } from '@/shared/api/client';

export function RegisterPage() {
  const doRegister = useAuth((s) => s.register);
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({ resolver: zodResolver(registerSchema) });

  const onSubmit = async (data: RegisterForm) => {
    setServerError(null);
    try {
      await doRegister(data);
      navigate('/', { replace: true });
    } catch (e) {
      setServerError(e instanceof ApiException ? e.message : 'Registrierung fehlgeschlagen.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-brand-700">ProofFlow</h1>
          <p className="mt-1 text-sm text-slate-500">Betrieb registrieren</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-xl bg-white p-6 shadow-sm">
          {serverError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</div>
          )}
          <Input label="Firmenname" {...register('organizationName')} error={errors.organizationName?.message} />
          <Input label="Ihr Name" {...register('displayName')} error={errors.displayName?.message} />
          <Input label="E-Mail" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
          <Input
            label="Passwort"
            type="password"
            autoComplete="new-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <p className="text-xs text-slate-500">Mindestens 12 Zeichen.</p>
          <Button type="submit" loading={isSubmitting} className="w-full">
            Registrieren
          </Button>
          <p className="text-center text-sm text-slate-500">
            Bereits registriert?{' '}
            <Link to="/anmelden" className="font-medium text-brand-600 hover:underline">
              Anmelden
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
